/**
 * ReportGalleryGrid — verdict-tinted CARD GALLERY for the Reports tab.
 *
 * Replaces the old vertical lineage LIST with a responsive gallery of
 * cards. Each generated report-type card carries:
 *
 *   - a verdict-tinted top band + soft accent glow (driven by the
 *     report's derived verdict tier → `bandColorClass`/`bandGlowColorVar`;
 *     neutral grey + no glow when the verdict is uncomputed),
 *   - the report type label + icon (REPORT_TYPE_REGISTRY shortLabel/icon),
 *   - the sentiment-first verdict pill (NEVER the raw 0-100 score — VS pack
 *     invariant; see SiteLandingPage.verdictLeak.regression.test),
 *   - a key metric (the report-card prose summary),
 *   - version + freshness ("v2.0 · 3d ago"),
 *   - inline actions: view / export / pin,
 *   - the version-history expand affordance ("{N} older"),
 *   - the [⋮] overflow menu (Delete) — separated from the navigate target.
 *
 * For offerable report types NOT yet run, a "Generate" launch card renders
 * IN THE SAME GRID (ReportTypeLaunchCard semantics + the wizard flow).
 *
 * This component owns ONLY presentation/layout. Every data wiring,
 * handler, prop, and `data-testid` from the prior inline lineage list is
 * preserved verbatim so the behavioural contract (and its tests) is
 * unchanged. The bulk of the redesign lives here to keep the
 * SiteLandingPage.tsx edit minimal (one import + one panel swap).
 */
import React, { useState } from 'react';
import {
  FileText,
  Leaf,
  Building2,
  Zap,
  Droplets,
  Cable,
  BarChart3,
  Loader2,
  MoreVertical,
  Trash2,
  ChevronRight,
  Eye,
  Download,
} from 'lucide-react';
import type { SiteRagReport } from '../../../types/siteRag';
import {
  REPORT_TYPE_REGISTRY,
  getOfferableReportTypes,
  type ReportTypeId,
  type ReportTypeDefinition,
} from '../../../config/reportTypes';
import type { ReportLineage } from '../../../lib/reportLineage';
import { getDisplayVerdict, type VerdictTier } from '../../../lib/reportVerdict';
import {
  bandColorClass,
  bandGlowColorVar,
  bandStatusPillVariant,
} from '../../shared/verdictVisuals';
import { StatusPill } from '../../ui/status-pill';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { getReportCardDecisionSummary } from '../../../lib/reportListSummary';
import { formatRelativeTime } from '../../../lib/portfolio/format';
import { Dialog, DialogContent } from '../../ui/dialog';
import { ReportReadinessPanel } from '../ReportReadinessPanel';
import {
  computeReadinessGaps,
  type ReadinessGapInputBase,
  type ReadinessGapAction,
} from '../../../lib/coverage/readinessGaps';
import {
  REPORT_CARD_DESCRIPTION_CLASS,
  REPORT_CARD_TITLE_CLASS,
} from '../reportCardTypography';

import { ReportTypeLaunchCard } from '../ReportTypeLaunchCard';
import { InflightRunProgressStrip } from '../InflightRunProgressStrip';
import type { ResolvedReportBranding } from '../../../lib/reportBranding';

const ICON_MAP: Record<string, React.ElementType> = {
  FileText,
  Leaf,
  Building2,
  Zap,
  Droplets,
  Cable,
};

/** #56 (don't-scare-users) — the GALLERY CARD band/glow only ever tints
 *  POSITIVE (green) verdicts. Amber / red / none all resolve to `null` ⇒
 *  neutral grey, no glow (identical to the uncomputed look): no scary red
 *  top-band or red glow on a feasibility card. This is SCOPED to the
 *  card-gallery colour decision only — the global verdict system (dots,
 *  PowerMixOrbit, portfolio band, `verdictVisuals` shared helpers,
 *  `getDisplayVerdict`) is unchanged; we still call those helpers, just feed
 *  them `null` for non-green tiers here. */
function tierToBandColor(
  tier: VerdictTier | null | undefined,
): 'green' | null {
  return tier === 'green' ? 'green' : null;
}

function resolveTypeIcon(typeDef: ReportTypeDefinition | undefined): React.ElementType {
  return ICON_MAP[typeDef?.icon ?? 'FileText'] ?? BarChart3;
}

export interface ReportGalleryGridProps {
  /** Generated report lineages grouped by type (insertion order preserved). */
  lineagesByType: Record<string, ReportLineage[]>;
  /** Render-prop for the orphan-inflight placeholder cards (runs whose
   *  type has no lineage yet). Owned by the host so the inflight wiring
   *  stays in one place. */
  renderOrphanInflightCards: () => React.ReactNode;
  /** In-flight generation run keyed by report type — when present on the
   *  lineage's lead card the card shows the live progress strip. */
  inflightRunByType: Record<string, { id: string; progress?: number; progressStage?: string; heartbeatAt?: string; startedAt?: string; estimatedDurationMs?: number } | undefined>;
  /** Audience-aware brand marks for the card header. */
  reportCardBranding: ResolvedReportBranding;
  /** Whether the current viewer is a Datacrate admin (gates the
   *  offerable-type set for the Generate launch cards). */
  isDatacrateAdmin: boolean;

  /** Lineage-id currently showing its version history. */
  expandedLineageId: string | null;
  setExpandedLineageId: (id: string | null) => void;
  /** Lineage-id whose [⋮] overflow menu is open. */
  reportMenuLineageId: string | null;
  setReportMenuLineageId: (updater: string | null | ((cur: string | null) => string | null)) => void;
  /** Lineage-id highlighted by a rerun deep-link (ring pulse). */
  highlightedRerunLineageId: string | null;


  /** Navigate into a report. */
  handleViewReport: (reportId: string) => void;
  /** Export: legacy no-revision reports open the print PDF
   *  (/print/{reportId}); immutable V2 revisions route to the report view,
   *  whose header menu serves the validated artifacts (2026-08-22). Barred
   *  reports open the report view with the state's own hint. */
  handleExportReport: (reportId: string) => void;
  /** Open the delete-report confirmation for a report. */
  setReportToDelete: (report: SiteRagReport) => void;
  /** Inline delete affordance on a history row. */
  handleDeleteReport: (e: React.MouseEvent | React.KeyboardEvent, report: SiteRagReport) => void;
  /** Resume the in-flight wizard for a type. */
  openInflightWizard: (typeId: ReportTypeId) => void;
  /** Cancel an in-flight generation run. */
  cancelReportGenerationJob: (runId: string) => void | Promise<void>;

  /** Launch the New Report wizard pre-seeded to a type (Generate cards). */
  onLaunchType: (typeId: ReportTypeId) => void;

  /** Read-only lock state for gating destructive / launch affordances. */
  lockReadOnly: boolean;
  lockHolderLabel: string;

  /** Render-prop for the launch/generated card actions (Report Variables button). */
  renderCardActions?: (typeId: string) => React.ReactNode;

  /** J8 — site-level inputs for the tier-aware readiness-gap computation.
   *  Drives the per-type readiness chips + the corrected readiness panel.
   *  When absent the chips degrade gracefully (not rendered). */
  readinessGapInput?: ReadinessGapInputBase;
  /** Routes a gap's resolution action onto the host's affordances
   *  (intent form / wizard / doc library / re-run). */
  onResolveGap?: (action: ReadinessGapAction) => void;
}

export function ReportGalleryGrid({
  lineagesByType,
  renderOrphanInflightCards,
  inflightRunByType,
  reportCardBranding,
  isDatacrateAdmin,
  expandedLineageId,
  setExpandedLineageId,
  reportMenuLineageId,
  setReportMenuLineageId,
  highlightedRerunLineageId,
  handleViewReport,
  handleExportReport,
  setReportToDelete,
  handleDeleteReport,
  openInflightWizard,
  cancelReportGenerationJob,
  onLaunchType,
  lockReadOnly,
  lockHolderLabel,
  renderCardActions,
  readinessGapInput,
  onResolveGap,
}: ReportGalleryGridProps) {
  const [readinessPanelOpen, setReadinessPanelOpen] = React.useState<ReportTypeId | null>(null);
  // Report types that already have at least one lineage — used to gate the
  // in-grid Generate launch cards so we never offer a type that's run.
  const generatedTypeIds = new Set(
    Object.entries(lineagesByType)
      .filter(([, lineages]) => lineages.length > 0)
      .map(([typeId]) => typeId),
  );
  const offerableNotRun = getOfferableReportTypes(isDatacrateAdmin).filter(
    (type) => !generatedTypeIds.has(type.id) && !inflightRunByType[type.id],
  );

  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0 || isBulkDeleting) return;
    
    // We confirm simply with a browser dialog for admin bulk actions
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} selected reports? This will only remove the reports (not the shared evidence).`)) {
      return;
    }

    setIsBulkDeleting(true);
    try {
      const { useStore } = await import('../../../store');
      let successCount = 0;
      for (const id of selectedIds) {
        try {
          await useStore.getState().deleteRagReport(id, 'report-only');
          successCount++;
        } catch (e) {
          console.warn(`Failed to delete report ${id}:`, e);
        }
      }
      
      const { toast } = await import('sonner');
      if (successCount === selectedIds.size) {
        toast.success(`Successfully deleted ${successCount} reports`);
      } else {
        toast.error(`Deleted ${successCount} reports, but ${selectedIds.size - successCount} failed.`);
      }
      
      setSelectedIds(new Set());
      setBulkMode(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {/* Admin Bulk Delete Controls */}
      {isDatacrateAdmin && (
        <div className="flex items-center justify-between bg-background-elevated p-3 rounded-lg border border-border">
          <div className="flex items-center gap-3">
            <h3 className="type-section text-text-primary m-0">Report Gallery</h3>
          </div>
          {bulkMode ? (
            <div className="flex items-center gap-2">
              <span className="type-meta text-text-muted">{selectedIds.size} selected</span>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => {
                  setBulkMode(false);
                  setSelectedIds(new Set());
                }}
                disabled={isBulkDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="mission-danger"
                size="sm"
                className="h-8"
                onClick={() => void handleBulkDelete()}
                disabled={selectedIds.size === 0 || isBulkDeleting}
              >
                {isBulkDeleting ? 'Deleting...' : 'Delete Selected'}
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-danger border-danger/40 hover:bg-danger/10 hover:text-danger"
              onClick={() => setBulkMode(true)}
              data-testid="reports-bulk-delete-toggle"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Bulk Delete
            </Button>
          )}
        </div>
      )}

      {/* Orphan in-flight runs (generating, no lineage yet) lead the grid.
           `renderOrphanInflightCards` returns an array; React renders an
           empty array as nothing, so the wrapper is inert when there are
           no orphans. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 empty:hidden" data-testid="reports-gallery-inflight">
        {renderOrphanInflightCards()}
      </div>

      {/* GENERATED REPORT CARDS + GENERATE LAUNCH CARDS — one responsive grid. */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        data-testid="reports-gallery-grid"
      >
        {Object.entries(lineagesByType).flatMap(([typeId, lineages]) => {
          const typeDef = REPORT_TYPE_REGISTRY[typeId as ReportTypeId];
          // Lead with the report TYPE short label (e.g. "Feasibility"),
          // not the implementation-noise full label.
          const typeShortLabel = typeDef?.shortLabel ?? 'Screening Assessment';
          const TypeIcon = resolveTypeIcon(typeDef);

          return lineages.map((lineage, lineageIdx) => {
            const { latest, history, lineageId } = lineage;
            const expanded = expandedLineageId === lineageId;
            const menuOpen = reportMenuLineageId === lineageId;
            const isSibling = lineages.length > 1 && lineageIdx > 0;

            // Sentiment-first verdict — NEVER the raw 0-100 score (VS pack).
            const verdict = getDisplayVerdict(latest);
            const bandColor = tierToBandColor(verdict.tier);
            const glowVar = bandGlowColorVar(bandColor);
            const verdictLabel =
              verdict.label && verdict.label !== '—' ? verdict.label : null;
            // #56 — only POSITIVE (green) verdicts surface a pill on the card.
            // Amber / red ("Challenging" / "Conditional") render NO pill so the
            // gallery never flashes a scary status badge at a glance.
            const showVerdictPill = bandColor === 'green' && Boolean(verdictLabel);

            // aurora-qa-3 (⑥) — a MEANINGFUL ≤3-sentence card blurb: prefer the
            // report's decision summary / verdict rationale (composed from the
            // conclusion when needed), falling back to the abstract's opening
            // COMPLETE sentences. Sentence-boundary truncation + the CSS
            // line-clamp-3 backstop on the <p> below keep it tight WITHOUT
            // cutting mid-word.
            const cardSummary = getReportCardDecisionSummary(latest, 3);
            const versionLabel = `v${latest.version || 1}.${latest.minorVersion || 0}`;
            const freshness = formatRelativeTime(latest.createdAt);


            const inflightRun = lineageIdx === 0 ? inflightRunByType[typeId] : undefined;
            const isGenerating = Boolean(inflightRun);
            const isRerunHighlighted = highlightedRerunLineageId === lineageId;

            const typeGapCount = readinessGapInput
              ? computeReadinessGaps({
                  reportTypeId: typeId as ReportTypeId,
                  ...readinessGapInput,
                  hasGeneratedReport: true,
                }).gaps.length
              : null;

            return (
              <div key={lineageId} className="flex flex-col gap-1">
                <article
                  className={`group relative flex flex-col rounded-lg overflow-hidden border bg-card shadow-card transition-shadow ${
                    bulkMode && selectedIds.has(latest.id) ? 'ring-2 ring-danger ring-offset-1 border-danger/50' : 'hover:shadow-[0_0_8px_var(--primary)]'
                  } ${
                    isGenerating ? 'border-primary/40 bg-primary/5' : 'border-border'
                  } ${isRerunHighlighted && !bulkMode ? 'ring-2 ring-warning ring-offset-2' : ''} ${bulkMode ? 'cursor-pointer' : ''}`}
                  data-testid={`lineage-card-${lineageId}`}
                  onClick={(e) => {
                    if (bulkMode) {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSelection(latest.id);
                    }
                  }}
                >
                  {/* Verdict-tinted top band + soft accent bloom (theme-aware
                       glow var; neutral grey + no glow when uncomputed). */}
                  <div
                    className={`h-1 w-full shrink-0 ${bandColorClass(bandColor)}`}
                    aria-hidden
                    data-testid={`lineage-verdict-band-${lineageId}`}
                    style={glowVar ? { boxShadow: `0 0 8px ${glowVar}` } : undefined}
                  />

                  {/* Hit target for the entire top area (view report).
                         Using an absolute overlay prevents text selection from
                         accidental clicks, but ensures the whole card acts as
                         a button without nesting <button>s. */}
                  <div
                    className={`absolute inset-0 z-0 ${bulkMode ? 'cursor-pointer' : 'cursor-pointer'}`}
                    data-testid={`lineage-click-overlay-${lineageId}`}
                    onClick={(e) => {
                      if (bulkMode) {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleSelection(latest.id);
                        return;
                      }
                      handleViewReport(latest.id);
                    }}
                    aria-label={bulkMode ? "Select report" : "View report"}
                  />

                  {isGenerating && inflightRun ? (
                    <div
                      className="p-3 space-y-2"
                      data-testid={`lineage-inflight-${lineageId}`}
                      role="status"
                      aria-live="polite"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {bulkMode && (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(latest.id)}
                              readOnly
                              className="w-4 h-4 accent-danger rounded shrink-0 mr-1 cursor-pointer pointer-events-none"
                            />
                          )}
                          <TypeIcon className="w-4 h-4 text-text-muted shrink-0" />
                          <span
                            className={`${REPORT_CARD_TITLE_CLASS} pl-0 truncate`}
                            data-testid={`lineage-type-label-${lineageId}`}
                          >
                            {lineageCardTitle(typeId as ReportTypeId, latest, typeShortLabel)}
                          </span>
                          <span className="type-caption shrink-0">· {versionLabel}</span>
                          {isSibling && (
                            <span className="type-meta font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/30">
                              Alt #{lineageIdx + 1}
                            </span>
                          )}
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`lineage-inflight-view-${lineageId}`}
                            onClick={() => openInflightWizard(typeId as ReportTypeId)}
                          >
                            View details
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="type-meta text-text-secondary hover:text-danger"
                            data-testid={`lineage-inflight-cancel-${lineageId}`}
                            onClick={() => { void cancelReportGenerationJob(inflightRun.id); }}
                            title="Cancel this report generation"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                      <InflightRunProgressStrip
                        runId={inflightRun.id}
                        progressStage={inflightRun.progressStage || 'Initializing…'}
                        progress={inflightRun.progress}
                        startedAt={inflightRun.startedAt}
                        estimatedDurationMs={inflightRun.estimatedDurationMs}
                        showTips
                        compact
                        testIdSuffix={`-${lineageId}`}
                      />
                    </div>
                  ) : (
                    <>
                      {/* Body — the navigate-into-detail target. */}
                      <div
                        role="button"
                        tabIndex={0}
                        className="w-full text-left p-3 flex flex-col gap-1.5 grow hover:bg-state-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                        onClick={() => handleViewReport(latest.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewReport(latest.id);
                          }
                        }}
                        data-testid={`lineage-row-${lineageId}`}
                      >
                        {/* Header — icon · type · version, brand marks right. */}
                        <div className="flex items-center gap-2 pr-7">
                          <TypeIcon className="w-4 h-4 text-text-muted shrink-0" />
                          <span
                            className={`${REPORT_CARD_TITLE_CLASS} pl-0 min-w-0 truncate`}
                            data-testid={`lineage-type-label-${lineageId}`}
                          >
                            {lineageCardTitle(typeId as ReportTypeId, latest, typeShortLabel)}
                          </span>
                          {isSibling && (
                            <span className="type-meta font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/30">
                              Alt #{lineageIdx + 1}
                            </span>
                          )}
                        </div>
                        
                        {renderCardActions && (
                          <div className="absolute top-3 right-10 z-10" onClick={(e) => e.stopPropagation()}>
                            {renderCardActions(typeId)}
                          </div>
                        )}

                        {/* Verdict pill (sentiment) + version · freshness. */}
                        <div className="flex items-center gap-2 flex-wrap pl-6">
                          {showVerdictPill && (
                            <StatusPill
                              variant={bandStatusPillVariant(bandColor)}
                              data-testid={`lineage-verdict-pill-${lineageId}`}
                            >
                              {verdictLabel}
                            </StatusPill>
                          )}
                          {typeGapCount !== null && !bulkMode && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReadinessPanelOpen(typeId as ReportTypeId);
                              }}
                              className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-border-subtle
                                         text-[11px] text-text-secondary hover:bg-background-elevated transition-colors
                                         z-10 shrink-0 relative"
                              aria-label={`${typeDef?.shortLabel ?? typeId} readiness`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                                typeGapCount === 0 ? 'bg-success' : 'bg-warning'
                              }`} />
                              {typeGapCount === 0 ? 'Ready' : `${typeGapCount} gap${typeGapCount > 1 ? 's' : ''}`}
                            </button>
                          )}
                          <span className="type-meta text-text-secondary">
                            {versionLabel} · {freshness}
                          </span>
                        </div>

                        {/* Key metric — first ~2 sentences of the prose summary
                             (FIX 4), with a 3-line clamp backstop so an
                             unpunctuated blob still can't run the card long. */}
                        {cardSummary && (
                          <p
                            className={`${REPORT_CARD_DESCRIPTION_CLASS} pl-6 line-clamp-3`}
                            data-testid={`lineage-summary-${lineageId}`}
                          >
                            {cardSummary}
                          </p>
                        )}
                      </div>

                      {/* Footer — inline actions: view / export / pin + history. */}
                      <div className="relative z-10 flex items-center gap-1 px-3 py-2 border-t border-border-subtle">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="type-meta text-text-secondary hover:text-primary"
                          onClick={(e) => { e.stopPropagation(); handleViewReport(latest.id); }}
                          data-testid={`lineage-action-view-${lineageId}`}
                          title="Open report"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="type-meta text-text-secondary hover:text-primary"
                          onClick={(e) => { e.stopPropagation(); handleExportReport(latest.id); }}
                          data-testid={`lineage-action-export-${lineageId}`}
                          title="Export report"
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> Export
                        </Button>
                        {history.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setExpandedLineageId(expanded ? null : lineageId); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setExpandedLineageId(expanded ? null : lineageId); } }}
                            className="ml-auto type-meta text-text-muted hover:text-primary px-1.5 py-0.5 rounded border border-border-subtle hover:border-border cursor-pointer"
                            data-testid={`lineage-history-toggle-${lineageId}`}
                            aria-expanded={expanded}
                          >
                            {history.length} older
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {/* [⋮] overflow menu — separated from the navigate target so a
                       misclick can't delete a report. */}
                  {!isGenerating && (
                    <div className="absolute top-3 right-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setReportMenuLineageId(menuOpen ? null : lineageId); }}
                        onBlur={() => setTimeout(() => setReportMenuLineageId((cur) => (cur === lineageId ? null : cur)), 150)}
                        className="p-1 rounded text-text-muted hover:text-text-primary hover:bg-state-hover transition-colors"
                        title="Report actions"
                        aria-label="Report actions"
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        data-testid={`lineage-menu-trigger-${lineageId}`}
                      >
                        <MoreVertical className="w-3.5 h-3.5" />
                      </button>
                      {menuOpen && (
                        <div
                          className="absolute right-0 top-full mt-1 z-20 min-w-[160px] rounded-md bg-background border border-border shadow-lg"
                          role="menu"
                          data-testid={`lineage-menu-${lineageId}`}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            onMouseDown={(e) => { e.preventDefault(); setReportMenuLineageId(null); setReportToDelete(latest); }}
                            className="w-full text-left px-3 py-2 type-meta text-danger hover:bg-danger/10 flex items-center gap-2"
                            data-testid={`lineage-menu-delete-${lineageId}`}
                          >
                            <Trash2 className="w-3 h-3" /> Delete report
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </article>

                {/* Version history — expandable. */}
                {expanded && history.length > 0 && (
                  <div className="ml-3 border-l-2 border-border-subtle pl-3 space-y-1" data-testid={`lineage-history-${lineageId}`}>
                    {history.map((report) => (
                      <button
                        key={report.id}
                        className="w-full text-left rounded-md border border-border p-2 hover:bg-muted transition-colors flex items-center gap-2"
                        onClick={() => handleViewReport(report.id)}
                      >
                        <Badge variant="outline" className="type-meta">
                          v{report.version || 1}.{report.minorVersion || 0}
                        </Badge>
                        <span className="type-meta text-text-secondary">
                          {new Date(report.createdAt).toLocaleDateString()}
                        </span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => handleDeleteReport(e, report)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleDeleteReport(e, report); }}
                          title="Delete this version"
                          aria-label="Delete report version"
                          className="ml-auto p-1 rounded text-text-muted hover:text-danger hover:bg-danger/10 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </span>
                        <ChevronRight className="w-3 h-3 text-text-primary shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          });
        })}

        {/* GENERATE launch cards — offerable types not yet run, in-grid. */}
        {offerableNotRun.map((type) => {
          const typeGapCount = readinessGapInput
            ? computeReadinessGaps({
                reportTypeId: type.id,
                ...readinessGapInput,
                hasGeneratedReport: false,
              }).gaps.length
            : null;

          const chip = typeGapCount !== null && !bulkMode ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setReadinessPanelOpen(type.id);
              }}
              className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-border-subtle
                         text-[11px] text-text-secondary hover:bg-background-elevated transition-colors
                         z-10 shrink-0 relative"
              aria-label={`${type.shortLabel ?? type.id} readiness`}
            >
              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                typeGapCount === 0 ? 'bg-success' : 'bg-warning'
              }`} />
              {typeGapCount === 0 ? 'Ready' : `${typeGapCount} gap${typeGapCount > 1 ? 's' : ''}`}
            </button>
          ) : null;

          const userActions = renderCardActions?.(type.id);

          return (
            <div key={`launch-${type.id}`} className="flex flex-col">
              <ReportTypeLaunchCard
                type={type}
                testId={`gallery-launch-card-${type.id}`}
                disabled={lockReadOnly}
                onClick={() => {
                  if (lockReadOnly) return;
                  onLaunchType(type.id);
                }}
                actions={
                  chip || userActions ? (
                    <div className="flex items-center gap-2 justify-end w-full">
                      {chip}
                      {userActions}
                    </div>
                  ) : undefined
                }
              />
            </div>
          );
        })}
      </div>
      {lockReadOnly && offerableNotRun.length > 0 && (
        <p className="sr-only" data-testid="reports-gallery-lock-note">
          {`Locked by ${lockHolderLabel} — read-only`}
        </p>
      )}

      {readinessPanelOpen && readinessGapInput && (
        <Dialog
          open={true}
          onOpenChange={(open) => { if (!open) setReadinessPanelOpen(null); }}
        >
          <DialogContent size="md" showCloseButton={false} className="p-0 overflow-hidden bg-background">
            <ReportReadinessPanel
              reportTypeId={readinessPanelOpen}
              reportTypeLabel={REPORT_TYPE_REGISTRY[readinessPanelOpen]?.label ?? readinessPanelOpen}
              gapInput={{ ...readinessGapInput, hasGeneratedReport: generatedTypeIds.has(readinessPanelOpen) }}
              onResolveGap={onResolveGap}
              onClose={() => setReadinessPanelOpen(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/** Type-local title resolver — mirrors the host's `lineageCardTitle`
 *  (custom-brief → user name, else the registry short label). Kept local
 *  so the gallery is self-contained and unit-testable. */
function lineageCardTitle(
  typeId: ReportTypeId,
  report: SiteRagReport,
  shortLabel: string,
): string {
  if (typeId === 'custom-brief') {
    const briefName = report.customBriefName?.trim();
    if (briefName) return briefName;
  }
  return shortLabel;
}
